import { style } from "@vanilla-extract/css";
import { primitiveTokens, semanticThemeTokens } from "../theme/ThemeTokens.css";

const { dropdown } = semanticThemeTokens;

const Z_INDEX = 2;

export const dropdownTrigger = style({
  width: "100%",
  position: "relative",
  zIndex: Z_INDEX + 1, // needs to be above the content to prevent shadow overlap
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: primitiveTokens.type.sm,
  fontWeight: 400,
  color: dropdown.trigger.default.color,
  backgroundColor: dropdown.trigger.default.backgroundColor,
  cursor: "pointer",
  height: primitiveTokens.sizing["10x"],
  paddingBlock: primitiveTokens.sizing["2x"],
  paddingInline: primitiveTokens.sizing["6x"],
  gap: primitiveTokens.sizing["4x"],
  border: `${primitiveTokens.borderWidth.default} solid transparent`,
  borderRadius: primitiveTokens.sizing["5x"],
  outline: "none",
  selectors: {
    '&[data-state="open"]': {
      backgroundColor: dropdown.content.backgroundColor,
      color: dropdown.trigger.active.color,
      borderBottomLeftRadius: primitiveTokens.sizing["0x"],
      borderBottomRightRadius: primitiveTokens.sizing["0x"]
    },
    "&:hover": {
      backgroundColor: dropdown.trigger.hover.backgroundColor,
      color: dropdown.trigger.hover.color
    },
    "&:focus": {
      backgroundColor: dropdown.trigger.active.backgroundColor,
      color: dropdown.trigger.active.color
    }
  }
});

export const triggerChevron = style({
  display: "flex",
  alignItems: "center",
  // Never let the chevron shrink when a long trigger label ellipsises.
  flexShrink: 0,
  transition: "transform 0.2s ease-in-out",
  selectors: {
    [`${dropdownTrigger}[data-state="open"] > &`]: {
      transform: "rotate(180deg)"
    }
  }
});

/** Radix variable is used to ensure the dropdown content width matches the trigger width
 * From the radix docs: https://www.radix-ui.com/primitives/docs/components/dropdown-menu#constrain-the-contentsub-content-size
 **/
export const dropdownContent = style({
  width: "var(--radix-dropdown-menu-trigger-width)",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: primitiveTokens.sizing["4x"],
  gap: primitiveTokens.sizing["3x"],
  backgroundColor: dropdown.content.backgroundColor,
  color: dropdown.content.color,
  fontWeight: 400,
  fontSize: primitiveTokens.type.sm,
  borderRadius: primitiveTokens.sizing["6x"],
  borderTopLeftRadius: primitiveTokens.sizing["0x"],
  borderTopRightRadius: primitiveTokens.sizing["0x"],
  backdropFilter: "blur(8px)",
  outline: "none",
  overflow: "hidden",
  zIndex: Z_INDEX,
  boxShadow: primitiveTokens.colors.shadow.popover
});

export const dropdownItem = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["2x"],
  padding: primitiveTokens.sizing["2x"],
  backgroundColor: dropdown.item.default.backgroundColor,
  color: dropdown.content.color,
  width: "100%",
  cursor: "pointer",
  outline: "none",
  selectors: {
    "&:hover, &[data-highlighted]": {
      backgroundColor: dropdown.item.hover.backgroundColor,
      color: dropdown.item.hover.color,
      borderRadius: primitiveTokens.radii.xs
    }
  }
});

export const navigationActive = style({
  backgroundColor: dropdown.trigger.active.backgroundColor
});

// Submenu panel: same surface as dropdownContent, but sized to its own items
// (not the root trigger's width) and rounded on all corners since it doesn't
// attach to a trigger edge.
export const dropdownSubContent = style([
  dropdownContent,
  {
    width: "max-content",
    minWidth: "160px",
    borderTopLeftRadius: primitiveTokens.sizing["6x"],
    borderTopRightRadius: primitiveTokens.sizing["6x"]
  }
]);

export const subTriggerChevron = style({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto"
});

// Detached panel (portalled, for non-select triggers like tabs): same shape
// as a submenu, stacked at the select-popover level since it escapes the
// trigger's stacking context.
export const dropdownDetachedContent = style([
  dropdownSubContent,
  {
    zIndex: 1050
  }
]);
